import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MsalProvider } from '@azure/msal-react';
import AppContext from './context/AppContext';
import routes from './routes';
import AuthVerification from './components/AuthVerification';
import UWEContext from './context/UWEContext';
import { UWEThemeProvider } from './context/UWEThemeContext';
import RouteSwitch from './components/RouteSwitch';
import initAppConfiguration from './hooks/initAppConfiguration';
import { msalInstance } from './msalConfig';
import { AD_CLIENT_ID, APP_NAME, BACKEND } from './constants';
import ModalContext from './context/ModalContext';
import { OpenCasesProvider } from './context/OpenCasesContext';
import { AuthProvider } from './context/AuthContext';
import { NotificationsProvider } from './context/NotificationsContext';
import ErrorBoundary from './components/ErrorBoundary';
import ErrorFallback from './components/ErrorFallback';
import ChatApp from "./views/ChatApp";
import { USE_AI } from "./constants";
import TopProgressBar from './components/TopProgressBar';
import CommandPalette, { useCommandPaletteShortcut } from './components/CommandPalette';
import { ToastProvider } from './components/ui/Toast';
import { useState } from 'react';

const ConfigurationProvider = initAppConfiguration({
  LOGO_SRC: `${BACKEND}/template-files/logo.png`, // "uwe_logo.svg",
  APP_NAME,
  AD_CLIENT_ID,
});

function App() {
  let app = (
    <ErrorBoundary fallback={ErrorFallback}>
      <ConfigurationProvider>
        <AuthProvider>
          <AppContexts>
            <UWEContext.Component>
              <OpenCasesProvider>
                <NotificationsProvider>
                {/* Global toast manager — anchors a portal at the top-right of
                    the viewport. Mounted high enough that anything inside
                    UWEContext can call `useToast()`. */}
                <ToastProvider>
                  <AuthVerification />
                  {/* Modern polish: NProgress-style top bar fades in on every
                      route change. Honours prefers-reduced-motion (uses an
                      opacity blink instead of a width animation when motion
                      is suppressed). Costs zero DOM when idle. */}
                  <TopProgressBar />
                  {/* Cmd/Ctrl + K command palette. Lives inside UWEContext so
                      it can list the tenant's workpages as nav targets, and
                      inside the Router so commands can `history.push()`. */}
                  <CommandPaletteHost />
                  {USE_AI && <ChatApp />}
                  <LocalizedRouteSwitch />
                </ToastProvider>
                </NotificationsProvider>
              </OpenCasesProvider>
            </UWEContext.Component>
          </AppContexts>
        </AuthProvider>
      </ConfigurationProvider>
    </ErrorBoundary>
  );

  if (msalInstance) {
    app = <MsalProvider instance={msalInstance}>{app}</MsalProvider>;
  }

  app = <UWEThemeProvider>{app}</UWEThemeProvider>;

  return app;
}

/** Remounts the routed view tree when the language changes. Many screens build
 *  translated config (sidebar tabs, table columns, form-editor labels) via the
 *  i18n.t() singleton inside a useMemo whose deps don't include the locale, so
 *  they wouldn't refresh on a language switch without a remount. Keying on the
 *  active language forces those memos to recompute. The providers above stay
 *  mounted, so auth/theme/contexts and the current URL are preserved. */
function LocalizedRouteSwitch() {
  const { i18n } = useTranslation();
  return <RouteSwitch routes={routes} key={i18n.language} />;
}

/** Small host component so the Cmd+K state + listener live INSIDE the
 *  UWEContext + Router scopes (the palette consumes both via hooks). */
function CommandPaletteHost() {
  const [open, setOpen] = useState(false);
  useCommandPaletteShortcut(setOpen);
  return <CommandPalette open={open} onClose={() => setOpen(false)} />;
}

function AppContexts({ children, auth }) {
  let element = children;
  element = <ModalContext.ProviderComponent>{element}</ModalContext.ProviderComponent>;
  element = <AppContext.Component>{element}</AppContext.Component>;

  return element;
}

export default App;
